import { ChangeDetectionStrategy, Component, HostBinding, OnInit, Signal, computed, inject, input, signal } from '@angular/core';

import { EventModel, EventOptionGroup, FootballScore } from '@frontend/sports/betting-offer/feature/model';
import { EpcotConfig } from '@frontend/sports/common/client-config-data-access';
import { SourceOfPick } from '@frontend/sports/option/pick/feature/tracking';
import { ParticipantImageComponent } from '@frontend/sports/participant-image/feature';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';

import { ScoreboardParticipantComponent } from '../event-details-common/scoreboard/scoreboard-participant.component';
import { OddIncrementDecrementComponent } from '../odd-increment-decrement/odd-increment-decrement.component';
import { ScoreDetails } from '../odd-increment-decrement/odd-increment-decrement.models';
import { CorrectScoreOptions, CorrectScoreService } from './correct-score.service';
import { MarketHelpComponent } from './market-help/market-help.component';
import { OptionComponent } from './option.component';

@Component({
    imports: [
        OddIncrementDecrementComponent,
        ParticipantImageComponent,
        OptionComponent,
        MarketHelpComponent,
        IconCustomComponent,
        ScoreboardParticipantComponent,
    ],
    selector: 'ms-correct-score-option-group-compact',
    templateUrl: './correct-score-option-group-compact.component.html',
    styleUrls: ['./correct-score-option-group-compact.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CorrectScoreOptionGroupCompactComponent implements OnInit {
    private correctScoreService = inject(CorrectScoreService);
    private epcotConfig = inject(EpcotConfig);

    event = input.required<EventModel>();
    tracking = input.required<string>();
    trackingModuleType = input('');
    optionGroup = input.required<EventOptionGroup>();
    sourceOfPick = input(SourceOfPick.Other);
    betDetails = input('');
    showMarketHelp = input.required<boolean>();
    eventHelpText = input<string | undefined>();
    crmPromotedOptionIdsMap = input<Record<string, number>>({});
    isEpcotEnabled = false;

    @HostBinding('class') className = 'correct-score-compact-container';

    private scoreBoardMap = signal({
        '0': { participantScore: 0 },
        '1': { participantScore: 0 },
    });

    private optionsLayout: Signal<CorrectScoreOptions> = computed(() => {
        return this.correctScoreService.getCorrectScoreOptions(this.optionGroup());
    });

    currentScoreOption = computed(() => {
        const option = this.optionsLayout().options.find((option) => {
            return (
                option?.player1Score === this.scoreBoardMap()[0].participantScore && option?.player2Score === this.scoreBoardMap()[1].participantScore
            );
        });
        return option ?? this.optionsLayout().rest[0];
    });

    getCurrentScores = computed(() => {
        const footballScore = this.event().scoreboard.getScore<FootballScore>();
        return [footballScore?.participant1 || 0, footballScore?.participant2 || 0];
    });

    constructor() {
        this.isEpcotEnabled = this.epcotConfig.isEnabled;
    }

    ngOnInit(): void {
        this.getCurrentScores().forEach((score, index) => {
            this.scoreBoardMap.update((scoreBoard) => ({ ...scoreBoard, [`${index}`]: { participantScore: score } }));
        });
    }

    changeScoreEvent(scoreDetails: ScoreDetails): void {
        this.scoreBoardMap.update((scoreBoard) => ({ ...scoreBoard, [`${scoreDetails.position}`]: { participantScore: scoreDetails.score } }));
    }
}
